import { create } from 'zustand';
import { authService, User, LoginCredentials } from '../services/authService';
import useGlobalCustomerStore from './globalCustomerStore';
import { canAccessSupportPage } from '../utils/permissions';
import usePermissionsStore from './permissionsStore';
import { useUIStore } from './uiStore';
import { analyticsService } from '../services/analyticsService';
import { AnalyticsEvents } from '../types/analytics';

// Helper function to fetch customerId and logoUrl from organizationId if needed
const enrichUserWithOrganizationData = async (user: User): Promise<User> => {
  // If user has organizationId, fetch the organization to get customerId and logoUrl
  if (user.organizationId) {
    try {
      const { organizationService } = await import('../services/organizationService');
      const orgResponse = await organizationService.getOrganization(user.organizationId);

      if (orgResponse.success && orgResponse.data) {
        // Store the thumbnail logo URL in the auth store for sidebar display (always sync, even if null)
        useAuthStore.setState({ organizationLogoUrl: orgResponse.data.logoUrlThumbnail || null });

        // Return enriched user with customerId if not already present
        if (!user.customerId && orgResponse.data.customerId) {
          return {
            ...user,
            customerId: orgResponse.data.customerId
          };
        }
      }
    } catch (error) {
      console.error('Failed to fetch organization data:', error);
    }
  }

  return user;
};

// Helper function to automatically select customer and all accounts for non-support users
const autoSelectCustomerForUser = async (user: User) => {
  // First, enrich user with organization data (customerId, logoUrl)
  const enrichedUser = await enrichUserWithOrganizationData(user);

  // Update the user in auth store if it was enriched
  if (enrichedUser.customerId && !user.customerId) {
    useAuthStore.setState({ user: enrichedUser });
  }

  // Only auto-select for users without access:support permission who have a customerId
  const hasAccessSupport = canAccessSupportPage(enrichedUser);
  if (enrichedUser.customerId && !hasAccessSupport) {
    try {
      // Load customers using the store's loadCustomers method
      await useGlobalCustomerStore.getState().loadCustomers();

      // Find the user's customer from the filtered list
      const customers = useGlobalCustomerStore.getState().customers;
      const customer = customers.find(c => c.id === enrichedUser.customerId);

      if (customer) {
        // Auto-select customer with "All Accounts" option
        const accountSelection = {
          type: 'all' as const,
          customerId: customer.id,
          customer: customer
        };
        useGlobalCustomerStore.getState().setSelectedAccount(accountSelection);
      }
    } catch (error) {
      console.error('Failed to auto-select customer:', error);
    }
  }
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionExpiresAt: string | null;
  organizationLogoUrl: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setError: (error: string) => void;
  setUser: (user: User | null) => void;
  setAuthenticated: (status: boolean) => void;
  setOrganizationLogoUrl: (logoUrl: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  // Start in the loading state: auth is "unknown" until AuthProvider's initial
  // checkAuth() resolves. Without this, route guards see isAuthenticated:false on
  // the first paint of a cold load and bounce deep links (e.g. /settings) to /login,
  // then to the default dashboard — losing the requested path and its query params.
  isLoading: true,
  error: null,
  sessionExpiresAt: null,
  organizationLogoUrl: null,

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.user) {
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          sessionExpiresAt: response.session?.expiresAt || null,
        });
        
        // Auto-select customer for regular users
        await autoSelectCustomerForUser(response.user);
        
        return true;
      } else {
        let errorMessage = 'Login failed';
        
        if (response.error?.message) {
          // Handle specific API error messages with user-friendly alternatives
          const apiError = response.error.message.toLowerCase();
          if (apiError.includes('invalid credentials') || apiError.includes('invalid email') || apiError.includes('invalid password')) {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          } else if (apiError.includes('account locked') || apiError.includes('locked')) {
            errorMessage = 'Your account has been temporarily locked. Please contact support or try again later.';
          } else if (apiError.includes('too many attempts')) {
            errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
          } else {
            errorMessage = response.error.message;
          }
        } else if (response.message) {
          errorMessage = response.message;
        }
        
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: errorMessage,
        });
        return false;
      }
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error instanceof Error) {
        // Handle network errors with user-friendly messages
        if (error.message.toLowerCase().includes('failed to fetch') || error.message.toLowerCase().includes('network')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.toLowerCase().includes('timeout')) {
          errorMessage = 'The request timed out. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      return false;
    }
  },

  logout: async () => {
    set({ isLoading: true });

    // Track logout event before clearing state
    analyticsService.track(AnalyticsEvents.AUTH_LOGOUT);

    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear auth state
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sessionExpiresAt: null,
        organizationLogoUrl: null,
      });

      // Clear customer selection
      useGlobalCustomerStore.getState().clearSelection();

      // Clear permissions cache
      usePermissionsStore.getState().clearPermissions();

      // Clear UI state
      useUIStore.getState().resetUIState();

      // Reset PostHog identity
      analyticsService.reset();
    }
  },

  checkAuth: async () => {
    set({ isLoading: true });

    try {
      const response = await authService.getCurrentUser();

      if (response.success && response.user) {
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          sessionExpiresAt: null, // getCurrentUser endpoint doesn't include session info
        });

        // Auto-select customer for regular users on page refresh
        await autoSelectCustomerForUser(response.user);
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          sessionExpiresAt: null,
        });
        // Clear customer selection when not authenticated
        useGlobalCustomerStore.getState().clearSelection();
        // Clear permissions cache
        usePermissionsStore.getState().clearPermissions();
      }
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null, // Don't show errors for auth checks
        sessionExpiresAt: null,
      });
      // Clear customer selection on auth error
      useGlobalCustomerStore.getState().clearSelection();
      // Clear permissions cache
      usePermissionsStore.getState().clearPermissions();
    }
  },

  clearError: () => {
    set({ error: null });
  },

  setError: (error: string) => {
    set({ error });
  },

  setUser: (user: User | null) => {
    set({ user });
  },

  setAuthenticated: (status: boolean) => {
    set({ isAuthenticated: status });
  },

  setOrganizationLogoUrl: (logoUrl: string | null) => {
    set({ organizationLogoUrl: logoUrl });
  },
}));